import { withDevtools } from '@angular-architects/ngrx-toolkit';
import { computed, inject } from '@angular/core';
import { patchState, signalStore, withComputed, withHooks, withMethods, withState } from '@ngrx/signals';
import { setEntities } from '@ngrx/signals/entities';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { I18nService } from '@pflegix/i18n';
import {
  CurrentActionType,
  patchErrorRequestStatus,
  setFetchFulfilled,
  setFetchPending,
  withPaginatedState,
  withRequestStatus,
} from '@pflegix/store';
import { ITaskIncludingTrackingInformation, TaskStatus } from '@pflegix/types';
import { AppIconEntity, AppIconEntityIconName, AppIconService, PanelGroupItem } from '@pflegix/ui';
import { Severity } from '@pflegix/util';
import { differenceInBusinessDays, format } from 'date-fns';
import { catchError, filter, pipe, switchMap, tap, throwError } from 'rxjs';
import { TaskStore } from '../../task-store';
import { TaskListWidgetApi } from '../api/task-list-widget.api';

const TASK_STATUS_TO_ICON_MAPPING: Record<TaskStatus, AppIconEntityIconName<AppIconEntity.TASK>> = {
  [TaskStatus.NEW]: 'newTask',
  [TaskStatus.PROCESSING]: 'inProgressTask',
  [TaskStatus.WAITING]: 'openTask',
  [TaskStatus.CLOSED]: 'closedTask',
};

function determineSeverityFromStatusAndExerciseDate({
  status,
  exerciseDate,
}: Pick<ITaskIncludingTrackingInformation, 'status' | 'exerciseDate'>): Severity {
  switch (status) {
    case TaskStatus.NEW: {
      return 'error';
    }
    case TaskStatus.PROCESSING: {
      return 'warn';
    }
    case TaskStatus.WAITING: {
      return 'info';
    }
    case TaskStatus.CLOSED: {
      return 'success';
    }
    default: {
      return 'info';
    }
  }
}

export const TaskListWidgetStore = signalStore(
  { protectedState: false },
  withDevtools('task-list-widget'),
  withPaginatedState<ITaskIncludingTrackingInformation>(),
  withState<{ getFilter: string }>({ getFilter: '' }),
  withRequestStatus(),
  withComputed(
    (
      { response },
      { entityMap } = inject(TaskStore),
      appIconService = inject(AppIconService),
      i18n = inject(I18nService)
    ) => ({
      renderItems: computed<PanelGroupItem[]>(() => {
        const res = response();
        const entityMapFromStore = entityMap();

        return res.docs.map((doc) => {
          const task = entityMapFromStore[doc._id];

          return {
            id: task._id,
            title: {
              text: task.description ?? 'Aufgabe',
              icon: appIconService.getTaskIcon(TASK_STATUS_TO_ICON_MAPPING[task.status]).icon,
            },
            subtitle: {
              text: task.exercise ?? '',
              icon: appIconService.getCommonIcon('info').icon,
            },
            description: [
              ...(task.exerciseDate
                ? [
                    {
                      // eslint-disable-next-line max-len
                      text: `in ${differenceInBusinessDays(new Date(task.exerciseDate), new Date())} Werktagen (${format(new Date(task.exerciseDate), 'EEEE')})`,
                      icon: appIconService.getCommonIcon('duration').icon,
                    },
                  ]
                : []),
              ...(task.editor
                ? [
                    {
                      text: `${task.editor.lastname}, ${task.editor.firstname}`,
                      icon: appIconService.getAppIcon(AppIconEntity.BUSINESS_PARTNER, 'manager').icon,
                    },
                  ]
                : [
                    {
                      text: 'nicht zugewiesen',
                      icon: appIconService.getAppIcon(AppIconEntity.BUSINESS_PARTNER, 'manager').icon,
                    },
                  ]),
            ],
            details: task.internalNotes,
            severity: determineSeverityFromStatusAndExerciseDate(task),
          };
        });
      }),
    })
  ),
  withMethods((store, taskListWidgetApi = inject(TaskListWidgetApi), taskStore = inject(TaskStore)) => ({
    setGetFilter: rxMethod<string>(pipe(tap((getFilter) => patchState(store, { getFilter })))),
    getTasks: rxMethod<string>(
      pipe(
        tap(() => patchState(store, setFetchPending())),
        switchMap((getFilter) =>
          taskListWidgetApi.getTasks(getFilter).pipe(
            tap((response) => {
              patchState(store, { response, ...setFetchFulfilled() });
              patchState(taskStore, setEntities(response.docs, { selectId: (entity) => entity._id }));
            }),
            catchError((error) => {
              patchErrorRequestStatus(store, error);
              return throwError(() => error);
            })
          )
        )
      )
    ),
    refetchTasks: rxMethod<CurrentActionType>(
      pipe(
        filter((action) => action === 'created' || action === 'updated'),
        tap(() => {
          patchState(store, setFetchPending());

          taskStore.setCurrentAction(undefined);
        }),
        switchMap(() => {
          const getFilter = store.getFilter();

          return taskListWidgetApi.getTasks(getFilter).pipe(
            tap((response) => {
              patchState(store, { ...setFetchFulfilled(), response });
              patchState(taskStore, setEntities(response.docs, { selectId: (entity) => entity._id }));
            }),
            catchError((error) => {
              patchErrorRequestStatus(store, error);
              return throwError(() => error);
            })
          );
        })
      )
    ),
  })),
  withHooks({
    onInit(store, taskStore = inject(TaskStore)) {
      store.getTasks(store.getFilter);
      store.refetchTasks(taskStore.currentAction);
    },
  })
);
